import React, { useMemo } from 'react'
import { Col, Container, Row, Stack } from 'react-bootstrap'

import { createState, IntegrationServiceId } from '../../../../common/integrations'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { Button } from '../../../../components/Button/Button'
import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../../store/user/user'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'

import '../IntegrationsTab.scss'
import { Link } from 'react-router-dom'
import { getIntegrationRedirectUri } from '../../../Integration/utils'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'

const HOLISTIPLAN_INFO_LINK = 'https://help.zocks.io/en/articles/12400657-holistiplan-tax-planning-integration'

export type HolistiplanIntegrationRowProps = { useIntegrationRedirectUri?: boolean }

export const HolistiplanIntegrationRow: React.FC<HolistiplanIntegrationRowProps> = ({ useIntegrationRedirectUri }) => {
  const accountId = useAppSelector(selectAccountId)

  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()

  const { data: currentUser } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })

  const hasHolistiplanIntegration = useMemo(() => {
    if (!currentUser?.integrations) {
      return false
    }
    return currentUser.integrations.some((i) => i.serviceId === IntegrationServiceId.HOLISTIPLAN)
  }, [currentUser])

  const authorizeHolistiplan = () => {
    const redirectUri = getIntegrationRedirectUri(IntegrationServiceId.HOLISTIPLAN)
    const state = useIntegrationRedirectUri ? createState(window.location.origin + '/dashboard/account/profile-settings/integrations') : createState()
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${IntegrationServiceId.HOLISTIPLAN}/enable?redirect_uri=${redirectUri}&state=${state}`
  }

  const uninstallHolistiplan = () => {
    if (!currentUser?.integrations) {
      return
    }
    const integration = currentUser.integrations.find((i) => i.serviceId === IntegrationServiceId.HOLISTIPLAN)
    deleteUserIntegration({
      accountId,
      payload: {
        userId: currentUser.id,
        integrationId: integration!.id,
      },
    })
  }

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.HOLISTIPLAN} width={48} height={48} />
            </div>
            <div>
              <h6>Holistiplan</h6>
              <div>Connect your Zocks account to Holistiplan.</div>
              <div>
                Your Firm Admin must enable Zocks in Holistiplan under Settings {'>'} Integrations. For more info, click{' '}
                <Link className='zocks-text-gray-900 text-decoration-underline' to={HOLISTIPLAN_INFO_LINK}>
                  here
                </Link>{' '}
                .
              </div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            <div className='connect-button-container'>
              {hasHolistiplanIntegration ? (
                <div className='connect-button-container'>
                  <Button variant='primary' onClick={uninstallHolistiplan} loading={isDeletingIntegration}>
                    Disconnect
                  </Button>
                  <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                    <i className='bi bi-check' />
                    Connected
                  </div>
                </div>
              ) : (
                <ScopedBlock
                  scope={{
                    scope: 'integration:connect',
                    superScope: ['integration:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button variant='primary' onClick={authorizeHolistiplan}>
                    Connect
                  </Button>
                </ScopedBlock>
              )}
            </div>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
